import React from 'react';
import { Box, CircularProgress, Paper, Rating, Stack, Typography } from '@mui/material';
import NotesIcon from '@mui/icons-material/Notes';
import { useDraggable } from '@dnd-kit/core';
import moment from 'moment';

import { IJobsAccountApplicant } from '../../services/serviceJobsAccount/interfaces';

export const ApplicantCard = ({ applicant, busy, onOpen }: {
  applicant: IJobsAccountApplicant;
  busy: boolean;
  onOpen: (applicant: IJobsAccountApplicant) => void;
}) => {
  const draggable = useDraggable({
    id: `application-${applicant.applicationId}`,
    data: { applicant },
    disabled: busy,
  });

  return (
    <Paper
      ref={draggable.setNodeRef}
      {...draggable.listeners}
      {...draggable.attributes}
      variant="outlined"
      aria-busy={busy}
      sx={{
        cursor: busy ? 'wait' : 'grab',
        opacity: busy ? 0.65 : 1,
        p: 1.5,
        position: 'relative',
        transform: draggable.transform
          ? `translate3d(${draggable.transform.x}px, ${draggable.transform.y}px, 0)`
          : undefined,
        zIndex: draggable.isDragging ? 4 : 1,
      }}
    >
      {busy ? <CircularProgress size={18} sx={{ position: 'absolute', right: 10, top: 10 }} /> : null}
      <Box
        component="button"
        type="button"
        onClick={() => onOpen(applicant)}
        sx={{
          background: 'none',
          border: 0,
          color: 'var(--ink)',
          cursor: 'pointer',
          font: 'inherit',
          fontWeight: applicant.isRead ? 700 : 900,
          p: 0,
          pr: 3,
          textAlign: 'left',
        }}
      >
        {!applicant.isRead ? (
          <Box component="span" aria-label="Unread" sx={{ color: 'var(--accent)', mr: 0.7 }}>●</Box>
        ) : null}
        {applicant.name || applicant.email || 'Applicant'}
      </Box>
      <Typography color="text.secondary" sx={{ fontSize: '0.78rem', mt: 0.4 }}>
        {applicant.jobTitle || 'Job listing'}
      </Typography>
      <Typography sx={{ fontSize: '0.8rem', mt: 1 }}>
        {applicant.dateSent ? moment(applicant.dateSent).format('MMM D, YYYY') : 'Date unavailable'}
      </Typography>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 1 }}>
        <Rating value={applicant.rating || 0} size="small" readOnly />
        <Stack direction="row" alignItems="center" spacing={0.4}>
          <NotesIcon sx={{ fontSize: 15 }} />
          <Typography variant="caption">{applicant.noteCount}</Typography>
        </Stack>
      </Stack>
    </Paper>
  );
};

export default ApplicantCard;
